import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faTimesCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { PatientService, sharedApiClient, patientViewPath } from '../fetcher';
import { Table, createTextColumn, createEnumColumn, createCustomColumn, createDateColumn, useTableState, MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle } from '@medprompt/shared';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import styles from './PatientSearchView.module.css';
import '../list-view-page.css';
import { StatusBadge } from '@medprompt/shared';

const PATIENT_SEARCH_STORAGE_KEY = 'patientSearchState';

const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId;
  return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
};

const formatDateOfBirth = (dob) => {
  if (!dob) return '';
  try {
    const [day, month, year] = dob.split('/');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dob;
  }
};

const parseDateOfBirth = (dob) => {
  if (!dob) return null;
  try {
    const [day, month, year] = dob.split('/');
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const formatSex = (sex) => {
  if (!sex) return '—';
  const normalized = sex.toUpperCase();
  if (normalized === 'M' || normalized === 'MALE') return 'Male';
  if (normalized === 'F' || normalized === 'FEMALE') return 'Female';
  return sex;
};

const PATIENT_STATUS_MAP = {
  ACTIVE: { label: 'Active', colorFamily: 'green' },
  DEDUCTION_PENDING: { label: 'Deduction Pending', colorFamily: 'amber' },
  INACTIVE: { label: 'Inactive', colorFamily: 'red' },
};

const PatientSearchViewController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientService = useMemo(() => new PatientService(sharedApiClient), []);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [noResultsError, setNoResultsError] = useState(false);
  const [isResultsView, setIsResultsView] = useState(false);

  const inputRef = useRef(null);

  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('PatientsSearchTable', {
    defaultSortState: { columnKey: 'name', direction: 'asc' }
  });

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.patientSearch);
  }, []);

  useEffect(() => {
    if (location.state?.preserveSearch) {
      const savedState = sessionStorage.getItem(PATIENT_SEARCH_STORAGE_KEY);
      if (savedState) {
        try {
          const { query, results } = JSON.parse(savedState);
          if (query && results && results.length > 1) {
            setSearchQuery(query);
            setSearchResults(results);
            setIsResultsView(true);
            setHasSearched(true);
          }
        } catch (e) {
          console.warn('Failed to restore patient search state:', e);
        }
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query) return;

    setIsSearching(true);
    setNoResultsError(false);
    setHasSearched(true);

    try {
      const results = await patientService.searchPatients(query);
      
      if (results.length === 0) {
        setNoResultsError(true);
        setSearchResults([]);
        setIsResultsView(false);
      } else if (results.length === 1) {
        sessionStorage.setItem(PATIENT_SEARCH_STORAGE_KEY, JSON.stringify({ query, results }));
        navigate(patientViewPath(results[0].nhsId, results[0].patientId), {
          state: { fromContext: 'search', patient: results[0] }
        });
      } else {
        sessionStorage.setItem(PATIENT_SEARCH_STORAGE_KEY, JSON.stringify({ query, results }));
        setSearchResults(results);
        setIsResultsView(true);
        setNoResultsError(false);
      }
    } catch (error) {
      console.error('Error searching patients:', error);
      setNoResultsError(true);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, patientService, navigate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setIsResultsView(false);
    setHasSearched(false);
    setNoResultsError(false);
    sessionStorage.removeItem(PATIENT_SEARCH_STORAGE_KEY);
    inputRef.current?.focus();
  }, []);

  const handleRowClick = useCallback((patient) => {
    setHighlightedRows(new Set([patient.nhsId]));
    navigate(patientViewPath(patient.nhsId, patient.patientId), {
      state: { fromContext: 'searchResults', patient }
    });
  }, [navigate, setHighlightedRows]);

  const columns = useMemo(() => [
    createTextColumn('name', 'Name', (p) => p.name || '', { sortable: true }),
    createCustomColumn(
      'nhsId',
      'NHS Number',
      (p) => p.nhsId || '',
      (nhsId) => <span className={styles['patient-nhs-cell']}>{formatNhsId(nhsId) || '—'}</span>,
      { sortable: false, filterable: true }
    ),
    createEnumColumn(
      'status',
      'Status',
      (p) => p.status || 'ACTIVE',
      Object.keys(PATIENT_STATUS_MAP),
      {
        renderer: (status) => {
          const entry = PATIENT_STATUS_MAP[status] || PATIENT_STATUS_MAP.ACTIVE;
          return (
            <StatusBadge
              label={entry.label}
              colorFamily={entry.colorFamily}
              size="normal"
            />
          );
        },
        sortable: true,
        compareFn: (a, b) => {
          const labelA = PATIENT_STATUS_MAP[a.status || 'ACTIVE']?.label || '';
          const labelB = PATIENT_STATUS_MAP[b.status || 'ACTIVE']?.label || '';
          return labelA.localeCompare(labelB);
        }
      }
    ),
    createDateColumn(
      'dateOfBirth',
      'Date of Birth',
      (p) => {
        const date = parseDateOfBirth(p.dateOfBirth);
        return date ? date.toISOString() : null;
      },
      {
        sortable: true,
        renderer: (value, patient) => (
          <span>{formatDateOfBirth(patient.dateOfBirth) || '—'}</span>
        ),
        compareFn: (a, b) => {
          const timeA = parseDateOfBirth(a.dateOfBirth)?.getTime() ?? 0;
          const timeB = parseDateOfBirth(b.dateOfBirth)?.getTime() ?? 0;
          return timeA - timeB;
        }
      }
    ),
    createTextColumn(
      'sex',
      'Sex',
      (p) => formatSex(p.sex),
      {
        sortable: true,
        renderer: (value) => <span>{value || '—'}</span>,
        compareFn: (a, b) => formatSex(a.sex).localeCompare(formatSex(b.sex))
      }
    ),
    createTextColumn('address', 'Address', (p) => p.address || '', { sortable: false, filterable: true })
  ], []);

  if (isResultsView) {
    return (
      <div className={`${styles['patient-search-view']} ${styles['patient-search-results-view']} list-view-page`}>
        <div className="header">
          <div className="header-title-text">
            <div className="header-title-with-help">
              <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.patientSearch}</h1>
              <HelpButton pageId={HELP_PAGE_IDS.patientSearch} />
            </div>
          </div>
          <div className="header-buttons">
            <button className={`${styles['header-button']} ${styles['clear-search-button']}`} onClick={handleClearSearch}>
              <FontAwesomeIcon icon={faTimesCircle} /> Clear search
            </button>
            <div className={`${styles['patient-search-bar']} ${styles['patients-search-bar-small']}`}>
              <div className={styles['patient-search-bar-input-container']}>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search for a patient..."
                  className={styles['patient-search-bar-input']}
                />
                {isSearching ? (
                  <div className={styles['patient-search-bar-spinner']}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </div>
                ) : (
                  <button className={styles['patient-search-bar-button']} onClick={handleSearch}>
                    <FontAwesomeIcon icon={faSearch} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles['patient-search-results-info']}>
          Found {searchResults.length} patient{searchResults.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </div>

        <div className={styles['patient-table-container']}>
          <Table
            id="PatientsSearchTable"
            data={searchResults}
            columns={columns}
            getRowKey={(p) => p.nhsId}
            sortState={tableSortState}
            onSortChange={setTableSortState}
            filterValues={tableFilterValues}
            onFilterChange={setTableFilterValues}
            disabledFilters={tableDisabledFilters}
            onDisabledFiltersChange={setTableDisabledFilters}
            paginationState={tablePaginationState}
            onPaginationChange={setTablePaginationState}
            onRowClick={handleRowClick}
            highlightedRows={highlightedRows}
            onHighlightChange={setHighlightedRows}
            emptyMessage="No patients found."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['patient-search-view']} ${styles['patient-search-centered-view']}`}>
      <div className={styles['patient-search-centered-container']}>
        <div className={styles['patient-search-title-row']}>
          <h1 className={styles['patient-search-title']}>Find a patient</h1>
          <HelpButton pageId={HELP_PAGE_IDS.patientSearch} />
        </div>
        <p className={styles['patient-search-subtitle']} style={{ fontStyle: 'italic' }}>
          Search by name or NHS number
        </p>
        
        <div className={`${styles['patient-search-bar']} ${styles['patient-search-bar-large']}`}>
          <div className={styles['patient-search-bar-input-container']}>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (noResultsError) setNoResultsError(false);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter patient name or NHS number..."
              className={styles['patient-search-bar-input']}
              autoFocus
            />
            {isSearching ? (
              <div className={styles['patient-search-bar-spinner']}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            ) : (
              <button 
                className={styles['patient-search-bar-button']} 
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
            )}
          </div>
        </div>

        {noResultsError && hasSearched && (
          <div className={styles['patient-search-error']}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            <span>No patients found on MedPrompt matching "{searchQuery}"</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientSearchViewController;
